import { useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Link2Off } from "lucide-react";
import Logo from "../components/Logo";
import { useLanguage } from "../context/LanguageContext";
import Field from "../components/Field";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token");
  const [values, setValues] = useState({ password: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setValues({ ...values, [k]: e.target.value });

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = {};
    if (values.password.length < 8) e.password = t.auth.errors.weakPassword;
    if (values.confirm !== values.password) e.confirm = t.auth.mismatch;
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: values.password });
      setDone(true);
      toast.success(t.auth.resetDone);
    } catch (err) {
      toast.error(err.response?.status === 400 ? t.auth.invalidLink : t.errors.generic);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="reset-password-page" className="pt-[72px] min-h-screen flex items-center justify-center px-5 py-16">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0F1015] p-8 lg:p-10">
        <div className="flex justify-center"><Logo /></div>

        {!token ? (
          <div data-testid="reset-invalid" className="mt-8 text-center">
            <Link2Off size={36} className="mx-auto text-slate-500" />
            <p className="mt-4 text-sm text-slate-300">{t.auth.invalidLink}</p>
            <Link to="/forgot-password" data-testid="reset-request-new-link" className="btn-outline mt-7 inline-flex text-[11px] font-mono2 uppercase">
              {t.auth.forgotTitle}
            </Link>
          </div>
        ) : done ? (
          <div data-testid="reset-success" className="mt-8 text-center">
            <CheckCircle2 size={36} className="mx-auto text-gold" />
            <p className="mt-4 text-sm text-slate-300">{t.auth.resetDone}</p>
            <Link to="/auth" data-testid="reset-to-login-button" className="btn-gold mt-7 inline-flex text-[11px] font-mono2 uppercase">
              {t.auth.backToLogin}
            </Link>
          </div>
        ) : (
          <>
            <h1 className="mt-8 text-center font-display text-xl font-bold uppercase">{t.auth.forgotTitle}</h1>
            <form className="mt-7 space-y-5" data-testid="reset-password-form" onSubmit={onSubmit} noValidate>
              <Field id="reset-password" label={t.auth.newPassword} error={errors.password} required>
                <input
                  id="reset-password"
                  data-testid="reset-password-input"
                  type="password"
                  className={`field-input ${errors.password ? "has-error" : ""}`}
                  value={values.password}
                  onChange={set("password")}
                  autoComplete="new-password"
                  placeholder={t.auth.passwordHint}
                />
              </Field>
              <Field id="reset-confirm" label={t.auth.confirmPassword} error={errors.confirm} required>
                <input
                  id="reset-confirm"
                  data-testid="reset-confirm-input"
                  type="password"
                  className={`field-input ${errors.confirm ? "has-error" : ""}`}
                  value={values.confirm}
                  onChange={set("confirm")}
                  autoComplete="new-password"
                />
              </Field>
              <button type="submit" data-testid="reset-submit-button" disabled={busy} className="btn-gold w-full text-xs font-mono2 uppercase disabled:opacity-60">
                {busy ? "…" : t.auth.resetBtn}
              </button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
